import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '@/context/api';
import { useAuth } from '@/context/auth';

import type { ProjectTypeRequest } from '../../schema/data-contracts';
import type { MutationProps } from '../types';

export const useProjectTypesQueryActions = () => {
  const { auth } = useAuth();
  const {
    projectTypesController: { v1ProjectTypesList, v1ProjectTypesUpdate },
    projectTypesWithCustomCultureController: {
      v1ProjectTypesList: v1ProjectTypesListWithCustomCulture,
    },
  } = useApi();

  const queryClient = useQueryClient();

  /**
   * EWM-1036: `onlyWithSelectableModel` asks the server for project types that still offer a model
   * a new estimation can be created from (published, latest published version, not archived). The
   * new-estimation wizard passes it so an archived model's project type stops being offered as a
   * tile that dead-ends. Every other caller needs the full list and leaves it off.
   */
  const useGetProjectTypes = (cultureName: string | undefined, onlyWithSelectableModel?: boolean) =>
    useQuery({
      enabled: Boolean(auth),
      queryKey: ['project-types', cultureName, onlyWithSelectableModel ?? false],
      queryFn: async () => {
        const query = onlyWithSelectableModel ? { onlyWithSelectableModel: true } : undefined;
        const { data } = cultureName
          ? await v1ProjectTypesListWithCustomCulture(query, {
              headers: { 'Accept-Language': cultureName },
            })
          : await v1ProjectTypesList(query);
        return data;
      },
    });

  const useUpdateProjectTypes = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['project-types-update'],
      mutationFn: async (projectTypes: ProjectTypeRequest[]) => {
        const { data } = await v1ProjectTypesUpdate(projectTypes);
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['project-types'] });
      },
    });

  return { useGetProjectTypes, useUpdateProjectTypes };
};
